import { getImage, getModelValue, getSurveyTitle } from '@aytm/app/stats/lib/stat-page'

import { removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'

import {
  assignAnswerVariant,
  resolveSigtestKeys,
  resolveSplitByValues
} from '@aytm/app/stats/page/question-views/cl/components/partial/lib/props'

const FULL_TEXT_LENGTH = 17

// Tracking the survey as a whole means one batch named after the survey; the waves are the
// attributes inside it, so there is nothing to compare across and significance is switched off.
const SURVEY_GROUP = 'survey'

const initBatchAnswer = ({ compacted, answer, batchKey, splitByRef }) => {
  compacted[batchKey] = compacted[batchKey] || { total: answer?.[batchKey]?.total }

  if (null != splitByRef && Object.hasOwn(answer?.[batchKey] || {}, splitByRef)) {
    compacted[batchKey][splitByRef] = answer[batchKey][splitByRef]
  }
}

const calcSurveyBatches = ({ compacted, answer, attributes, splitByRef, splitByValues, keys }) => {
  initBatchAnswer({ compacted, answer, batchKey: 'b1', splitByRef })

  return [
    {
      text: getSurveyTitle(),
      answerVariants: attributes.map(attr =>
        assignAnswerVariant({ compacted, answer, batchKey: 'b1', attr, splitByRef, splitByValues, keys })
      ),
      position: 1
    }
  ]
}

const calcEntityBatches = ({ compacted, answer, entities, attributes, splitByRef, splitByValues, keys }) =>
  entities.map(item => {
    const batchKey = `b${item.position}`

    initBatchAnswer({ compacted, answer, batchKey, splitByRef })

    const batch = {
      text: item.text,
      answerVariants: attributes.map(attr =>
        assignAnswerVariant({ compacted, answer, batchKey, attr, splitByRef, splitByValues, keys })
      ),
      position: item.position
    }

    if (null != item.image) batch.image = getImage(item.image)

    return batch
  })

// The compare-items payload a tracker-lab partial draws. It differs from the concept-lab one in
// what it compares: whole-survey tracking collapses to a single batch and drops the analysis
// controls that only mean something across several.
export const calcPartialProps = ({
  id,
  key,
  text,
  answer,
  entities,
  attributes,
  splitBy,
  group,
  keys = resolveSigtestKeys(),
  percentDecimals = getModelValue('percentDecimals')
}) => {
  const splitByRef = splitBy?.ref
  const splitByValues = resolveSplitByValues(splitByRef)

  const isSurvey = SURVEY_GROUP === group
  const compacted = { total: answer?.total }

  const batches = isSurvey
    ? calcSurveyBatches({ compacted, answer, attributes: attributes || [], splitByRef, splitByValues, keys })
    : calcEntityBatches({
        compacted,
        answer,
        entities: entities || [],
        attributes: attributes || [],
        splitByRef,
        splitByValues,
        keys
      })

  return {
    id,
    key,
    title: `${key.toUpperCase()}: ${text}`,
    type: 'compare-items',
    format: {
      preset: 'all',
      attribute: 0,
      analysisMode: isSurvey ? 'none' : 'sigtest',
      itemName: 'entity',
      exportType: 'tracker_lab',
      showImages: false,
      fullTexts: batches.some(batch => FULL_TEXT_LENGTH < removeTagsFromText(batch.text).length),
      showScrollToDetails: false,
      enableSigtestAnalysis: !isSurvey,
      enablePresets: !isSurvey,
      enableRankBy: !isSurvey,
      enableSplitBy: false
    },
    answer: compacted,
    batches,
    splitBy: splitBy || { ref: null, group: 'items' },
    percentDecimals
  }
}
